import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import socialLimitsService from '../services/socialLimitsService';

// Hook for connected accounts limits
export const useConnectedAccountsLimit = () => {
  const { user } = useAuth();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['socialLimits', 'connectedAccounts', user?.id],
    queryFn: () => socialLimitsService.checkConnectedAccountsLimit(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    ...data,
    isLoading,
    refetch,
    canConnect: data?.allowed ?? true,
  };
};

// Hook for monthly posts limits (counts content pieces using PostGroup logic)
export const useMonthlyPostsLimit = () => {
  const { user } = useAuth();
  
  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ['socialLimits', 'monthlyPosts', user?.id],
    queryFn: () => socialLimitsService.checkMonthlyPostsLimit(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  // Debug logging
  if (error) {
    console.error('Monthly posts limit error:', error);
  }

  const trackPublish = async () => {
    try {
      await socialLimitsService.trackPostPublish();
      refetch(); // Refresh the data after tracking
      return true;
    } catch (error) {
      console.error('Failed to track post publish:', error);
      return false;
    }
  };

  return {
    ...data,
    isLoading,
    refetch,
    trackPublish,
    canPublish: data?.allowed ?? true,
  };
};

// Hook for scheduled queue limits (counts individual scheduled posts)
export const useScheduledQueueLimit = () => {
  const { user } = useAuth();
  
  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ['socialLimits', 'scheduledQueue', user?.id],
    queryFn: () => socialLimitsService.checkScheduledQueueLimit(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  // Debug logging
  if (error) {
    console.error('Scheduled queue limit error:', error);
  }

  return {
    ...data,
    isLoading,
    refetch,
    canSchedule: data?.allowed ?? true,
  };
};

// Hook for team members limits
export const useTeamMembersLimit = () => {
  const { user } = useAuth();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['socialLimits', 'teamMembers', user?.id],
    queryFn: () => socialLimitsService.checkTeamMembersLimit(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    ...data,
    isLoading,
    refetch,
    canAddMember: data?.allowed ?? true,
  };
};

// Hook for storage limits
export const useStorageLimit = () => {
  const { user } = useAuth();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['socialLimits', 'storage', user?.id],
    queryFn: () => socialLimitsService.checkStorageLimit(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const canUploadFile = async (fileSizeBytes: number): Promise<boolean> => {
    try {
      const result = await socialLimitsService.checkFileUploadLimit(fileSizeBytes);
      return result.allowed;
    } catch (error) {
      console.error('Failed to check file upload limit:', error);
      return false;
    }
  };

  return {
    ...data,
    isLoading,
    refetch,
    canUploadFile,
  };
};

// Hook for analytics history limits
export const useAnalyticsHistoryLimit = () => {
  const { user } = useAuth();
  
  const { data, isLoading } = useQuery({
    queryKey: ['socialLimits', 'analyticsHistory', user?.id],
    queryFn: () => socialLimitsService.checkAnalyticsHistoryLimit(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    ...data,
    isLoading,
  };
};

// Hook for all social media limits
export const useAllSocialLimits = () => {
  const { user } = useAuth();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['socialLimits', 'all', user?.id],
    queryFn: () => socialLimitsService.getAllSocialLimits(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    limits: data,
    isLoading,
    refetch,
  };
};

// Hook for upgrade suggestions
export const useUpgradeSuggestions = () => {
  const { user } = useAuth();
  
  const { data, isLoading } = useQuery({
    queryKey: ['socialLimits', 'upgradeSuggestions', user?.id],
    queryFn: () => socialLimitsService.getUpgradeSuggestions(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    suggestions: data,
    isLoading,
    needsUpgrade: data?.needs_upgrade ?? false,
  };
};

// Convenience hooks for simple boolean checks
export const useCanConnectAccount = () => {
  const { user } = useAuth();
  
  const { data, isLoading } = useQuery({
    queryKey: ['socialLimits', 'canConnect', user?.id],
    queryFn: () => socialLimitsService.canConnectAccount(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });

  return {
    canConnect: data?.allowed ?? true,
    isLoading,
  };
};

export const useCanPublishPost = () => {
  const { user } = useAuth();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['socialLimits', 'canPublish', user?.id],
    queryFn: () => socialLimitsService.canPublishPost(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 1 * 60 * 1000,
  });

  return {
    canPublish: data?.allowed ?? true,
    isLoading,
    refetch,
  };
};

export const useCanSchedulePost = () => {
  const { user } = useAuth();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['socialLimits', 'canSchedule', user?.id],
    queryFn: () => socialLimitsService.canSchedulePost(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 1 * 60 * 1000,
  });

  return {
    canSchedule: data?.allowed ?? true,
    isLoading,
    refetch,
  };
};

export const useCanAddTeamMember = () => {
  const { user } = useAuth();
  
  const { data, isLoading } = useQuery({
    queryKey: ['socialLimits', 'canAddMember', user?.id],
    queryFn: () => socialLimitsService.canAddTeamMember(),
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    canAddMember: data?.allowed ?? true,
    isLoading,
  };
};